type NavigationLinkLike = {
  label?: string;
  linkType?: string;
  pageId?: string;
  url?: string;
  href?: string;
  children?: NavigationLinkLike[];
};

type FooterLinkLike = {
  label?: string;
  linkType?: string;
  pageId?: string;
  url?: string;
  href?: string;
};

type NavigationConfigLike = {
  links?: NavigationLinkLike[];
  /**
   * Set the first time a human saves the nav from an editor. From then on the
   * stored links are the source of truth: labels are not re-derived from page
   * titles and missing core pages are not auto-appended. Dead internal links
   * are still pruned and page URLs still sync.
   */
  manuallyManaged?: boolean;
} & Record<string, unknown>;

type FooterConfigLike = {
  linkColumns?: {
    title?: string;
    links?: FooterLinkLike[];
  }[];
} & Record<string, unknown>;

interface JobsiteNavigationPage {
  id: string;
  path: string;
  title: string;
  type?: string | null;
  navLabel?: string | null;
  navOrder?: number | null;
  order?: number | null;
  isHomePage?: boolean;
  isPublished?: boolean;
  includeInNav?: boolean;
  includeInFooter?: boolean;
}

const CORE_PAGE_TYPES = new Set([
    // "home" is intentionally excluded — the logo serves as the home link
    "about",
    "contact",
    "services",
    "reviews",
    "faq",
    "gallery",
    "blog",
    "location_index",
  ]),
  normalizePath = (path: string): string => {
    if (!path) {
      return "/";
    }
    if (path === "/") {
      return "/";
    }
    return path.replace(/\/+$/u, "") || "/";
  },
  isInternalPath = (url?: string | null): boolean => Boolean(url && url.startsWith("/") && !url.startsWith("//")),
  getLinkUrl = (link: { url?: string; href?: string }): string | undefined => link.url ?? link.href,
  buildPageMaps = (pages: JobsiteNavigationPage[]) => {
    const visiblePages = pages.filter((page) => page.isPublished !== false),
      byId = new Map(visiblePages.map((page) => [page.id, page])),
      byPath = new Map(visiblePages.map((page) => [normalizePath(page.path), page]));

    return { byId, byPath, visiblePages };
  },
  resolvePageForLink = (
    link: { pageId?: string; url?: string; href?: string },
    byId: Map<string, JobsiteNavigationPage>,
    byPath: Map<string, JobsiteNavigationPage>,
  ): JobsiteNavigationPage | null => {
    if (link.pageId) {
      return byId.get(link.pageId) ?? null;
    }

    const linkUrl = getLinkUrl(link);
    if (isInternalPath(linkUrl)) {
      return byPath.get(normalizePath(linkUrl!)) ?? null;
    }

    return null;
  },
  materializeNavigationLink = (
    link: NavigationLinkLike,
    byId: Map<string, JobsiteNavigationPage>,
    byPath: Map<string, JobsiteNavigationPage>,
    preserveLabels: boolean,
  ): NavigationLinkLike | null => {
    // Dropdowns keep their own (group) label — "Service Areas", "Services" — but
    // Always re-materialize children so child labels track the current page
    // Title (unless a human manages the nav) and links to removed pages are
    // Pruned.
    if (link.linkType === "dropdown") {
      const visibleChildren = (link.children ?? [])
          .map((child) => materializeNavigationLink(child, byId, byPath, preserveLabels))
          .filter(Boolean) as NavigationLinkLike[],

        page = resolvePageForLink(link, byId, byPath),
        linkUrl = getLinkUrl(link),
        // A dropdown survives if it has visible children or points somewhere itself.
        // A dead internal target (page gone) is not a valid landing; an external url is.
        ownUrl = page ? page.path : (isInternalPath(linkUrl) ? undefined : linkUrl);
      if (visibleChildren.length === 0 && (ownUrl ?? "") === "") {
        return null;
      }

      if (page) {
        const { href: _href, ...restLink } = link;
        return { ...restLink, children: visibleChildren, url: page.path };
      }
      return { ...link, children: visibleChildren };
    }

    // Leaf links that resolve to a page derive their label from the page, so a
    // Page-title edit shows up in the nav (and the URL stays in sync). Stored
    // Labels are no longer frozen at generation time — but once a human manages
    // The nav, their label wins over the derived one.
    const page = resolvePageForLink(link, byId, byPath);
    if (page) {
      const { href: _href, ...restLink } = link,
        storedLabel = typeof link.label === "string" ? link.label.trim() : "";
      return {
        ...restLink,
        label:
          preserveLabels && storedLabel ? storedLabel : page.navLabel?.trim() || page.title,
        url: page.path,
      };
    }

    // A leaf link to an internal page that no longer exists is pruned; external
    // And static links are kept as-is.
    if ((link.pageId ?? "") !== "" || isInternalPath(getLinkUrl(link))) {
      return null;
    }

    return { ...link };
  },
  materializeFooterLink = (
    link: FooterLinkLike,
    byId: Map<string, JobsiteNavigationPage>,
    byPath: Map<string, JobsiteNavigationPage>,
  ): FooterLinkLike | null => {
    const page = resolvePageForLink(link, byId, byPath);

    if (page) {
      const { href: _href, ...restLink } = link;
      return {
        ...restLink,
        url: page.path,
      };
    }

    if ((link.pageId ?? "") !== "" || isInternalPath(getLinkUrl(link))) {
      return null;
    }

    return { ...link };
  },
  collectLinkedPages = (links: NavigationLinkLike[]): Set<string> => {
    const linked = new Set<string>(),

      visit = (items: NavigationLinkLike[]) => {
        for (const item of items) {
          if (item.pageId) {
            linked.add(`id:${item.pageId}`);
          }
          const linkUrl = getLinkUrl(item);
          if (isInternalPath(linkUrl)) {
            linked.add(`path:${normalizePath(linkUrl!)}`);
          }
          if (item.children?.length) {
            visit(item.children);
          }
        }
      };

    visit(links);
    return linked;
  },
  isCorePage = (page: JobsiteNavigationPage): boolean => {
    if (page.type === undefined || page.type === null || page.type === "") {
      return false;
    }
    return CORE_PAGE_TYPES.has(page.type);
  },

  resolveNavigationConfigForPages = <T extends NavigationConfigLike>(
    navigationConfig: T,
    pages: JobsiteNavigationPage[],
  ): T => {
    const { visiblePages, byId, byPath } = buildPageMaps(pages),
      manuallyManaged = navigationConfig.manuallyManaged === true,
      resolvedLinks = (navigationConfig.links ?? [])
        .map((link) => materializeNavigationLink(link, byId, byPath, manuallyManaged))
        .filter(Boolean) as NavigationLinkLike[];

    // A human-managed nav is exactly what its owner saved: no auto-appended
    // Core pages, so a deleted item stays deleted.
    if (manuallyManaged) {
      return { ...navigationConfig, links: resolvedLinks };
    }

    const linkedPages = collectLinkedPages(resolvedLinks),
      missingCoreLinks = visiblePages
        .filter((page) => page.includeInNav !== false && isCorePage(page))
        .sort(
          (left, right) =>
            (left.navOrder ?? left.order ?? Number.MAX_SAFE_INTEGER) -
            (right.navOrder ?? right.order ?? Number.MAX_SAFE_INTEGER),
        )
        .filter((page) => (
          !linkedPages.has(`id:${page.id}`) && !linkedPages.has(`path:${normalizePath(page.path)}`)
        ))
        .map((page) => ({
          label: page.navLabel?.trim() || page.title,
          linkType: "url",
          url: page.path,
        }));

    return {
      ...navigationConfig,
      links: [...resolvedLinks, ...missingCoreLinks],
    };
  },

  /**
   * A transparent nav is fixed and overlays the page's first section — only
   * imagery can host it. Pages that don't open with a Hero (old generated
   * layouts with Breadcrumb first, owner-built pages starting with content)
   * demote to solid at render time, so stale page data never renders a
   * white-on-white header. EmergencyBand is skipped: it pads for the fixed nav
   * itself and renders nothing unless the business toggle is on.
   */
  resolveNavigationStyleForPage = <T extends string | undefined>(
    style: T,
    content: { type?: string }[] | undefined | null,
  ): T | "solid" => {
    if (style !== "transparent") {
      return style;
    }
    const first = (content ?? []).find((node) => node?.type !== "EmergencyBand");
    if (first?.type === "Hero") {
      return style;
    }
    return "solid";
  },

  resolveFooterConfigForPages = <T extends FooterConfigLike>(
    footerConfig: T,
    pages: JobsiteNavigationPage[],
  ): T => {
    const { byId, byPath } = buildPageMaps(pages);

    return {
      ...footerConfig,
      linkColumns: (footerConfig.linkColumns ?? [])
        .map((column) => ({
          ...column,
          links: (column.links ?? [])
            .map((link) => materializeFooterLink(link, byId, byPath))
            .filter(Boolean) as FooterLinkLike[],
        }))
        .filter((column) => (column.links ?? []).length > 0),
    };
  };

export type { JobsiteNavigationPage };
export {
  resolveNavigationConfigForPages,
  resolveNavigationStyleForPage,
  resolveFooterConfigForPages,
};
